import React from 'react';
import { useTranslation } from 'next-i18next';
import { isMobile } from 'react-device-detect';
import classnames from 'classnames';

import type { SliderType } from '@components/ui';
import {
  Icon, Slide, Slider
} from '@components/ui';
import { useAnalytics } from '@hooks/useAnalytics';
import { useMediaQuery } from '@hooks/useMediaQuery';

import PromotionCard from '../PromotionCard/PromotionCard';
import PromotionCardSkeleton from '../PromotionCard/PromotionCardSkeleton';

import type { Props as PromotionCardProps } from '../PromotionCard/PromotionCard';

import type { IPromotionCard } from 'types';

import styles from './PromotionsCarousel.module.scss';

const LOADING_SLIDES_COUNT = 7;

interface PromotionCardOverrideProps extends Partial<PromotionCardProps>{}

interface Props {
  className?: string,
  sliderClassName?: string,
  slideClassName?: string,
  cardClassName?: string,
  //
  loading?: boolean,
  //
  promotions?: IPromotionCard[],
  selectedCode?: string,
  //
  withNavigation?: boolean,
  withDescription?: boolean,
  withBackground?: boolean,
  fill?: boolean,
  cardProps?: PromotionCardOverrideProps,
  //
  onSelect?: (promotion: IPromotionCard) => void
}

const PromotionsCarousel = (props: Props) => {
  const {
    //
    className,
    sliderClassName,
    slideClassName,
    cardClassName,
    //
    loading,
    //
    promotions,
    selectedCode,
    //
    withNavigation = true,
    withBackground = true,
    withDescription,
    fill,
    cardProps,
    //
    onSelect = () => {},
    //
    ...rest
  } = props;

  // Refs

  const swiperRef = React.useRef<SliderType>(null);

  // Hooks

  const { t } = useTranslation();
  const analytics = useAnalytics();

  const isBreakpoint = useMediaQuery(`(max-width: ${styles.breakpoint})`);

  // State Hooks

  const [isFirstSlide, setIsFirstSlide] = React.useState(false);
  const [isLastSlide, setIsLastSlide] = React.useState(false);

  // Effects

  React.useEffect(() => {
    const selectedPromotionIndex = promotions.findIndex(({ code }) => code === selectedCode);
    if (selectedPromotionIndex !== -1) {
      scrollSlideIntoView(swiperRef.current, selectedPromotionIndex);
    }
  }, [promotions, selectedCode]);

  // Handlers

  const onPromotionEnter = (promotion: IPromotionCard, index: number) => {
    analytics.viewPromotion(promotion, index + 1);
  };

  const onPromotionSelect = (promotion: IPromotionCard, index: number) => {
    analytics.selectPromotion(promotion, index + 1);
  };

  // Helpers

  const scrollSlideIntoView = (swiper: SliderType, activeIndex: number) => {
    const { wrapperEl, slides } = swiper;
    const activeSlideEl = slides[activeIndex] as HTMLElement;
    if (wrapperEl.clientWidth < activeSlideEl.offsetLeft + activeSlideEl.clientWidth) {
      swiper.slideTo(activeIndex - 1, 0);
    }
  };

  // Render

  return (
    <div className={classnames(styles.root, className)}>

      {
        (withNavigation && withBackground) && (
          <>
            {/* Fade - Left */}
            <div
              className={classnames(
                styles.fade,
                styles.left,
                { [styles.hidden]: loading || isFirstSlide },
                { [styles.small]: !isLastSlide }
              )}
              onClick={() => {
                swiperRef.current.slidePrev();
              }}
            />

            {/* Fade - Right */}
            <div
              className={classnames(
                styles.fade,
                styles.right,
                { [styles.hidden]: loading || isLastSlide }
              )}
              onClick={() => {
                swiperRef.current.slideNext();
              }}
            />
          </>
        )
      }

      {/* Slider */}
      <Slider
        //
        className={classnames(
          styles.carousel,
          { [styles.withBackground]: withBackground },
        )}
        wrapperClassName={styles.carouselWrapper}
        sliderClassName={classnames(
          styles.slider,
          { [styles.withBackground]: withBackground },
          sliderClassName
        )}
        prevClassName={classnames(styles.navButton, styles.prev, { [styles.hidden]: loading })}
        nextClassName={classnames(styles.navButton, styles.next, { [styles.hidden]: loading })}
        scrollbarTrackClassName={styles.scrollbar}
        scrollbarHandleClassName={styles.handle}
        //
        spaceBetween={isBreakpoint ? 8 : (cardProps?.inline ? 24 : 16)}
        slidesOffsetBefore={isBreakpoint ? 16 : (withDescription ? 224 : 24)}
        slidesOffsetAfter={isBreakpoint ? 16 : 24}
        //
        slidesPerView="auto"
        slidesPerGroup={1}
        speed={350}
        //
        navigation={withNavigation}
        scrollbar
        allowTouchMove={isMobile}
        preventClicks={isMobile}
        preventClicksPropagation={isMobile}
        noSwiping={!isMobile}
        //
        watchSlidesProgress
        //
        onRealIndexChange={(swiper) => {
          setIsFirstSlide(swiper?.isBeginning);
          setIsLastSlide(swiper?.isEnd);
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          setIsFirstSlide(swiperRef?.current.isBeginning);
          setIsLastSlide(swiperRef?.current.isEnd);
        }}
        //
        height={fill ? 'fill' : 'auto'}
        data-test-id="promotions-carousel"
        //
        {...rest}
      >

        {/* Description */}
        {
          !withDescription
            ? null
            : (
              <div slot="wrapper-start" className={styles.description}>
                <Icon
                  name="discount"
                  size={50}
                  strokeWidth={1}
                />
                <div
                  className={styles.text}
                  dangerouslySetInnerHTML={{ __html: t('PROMOTIONS.CAROUSEL.DESCRIPTION') }}
                />
              </div>
            )
        }

        {/* Slides */}
        {
          loading
            ? (
              Array.from(Array(LOADING_SLIDES_COUNT)).map((_, index) => (
                <Slide
                  key={`loading-${index}`}
                  className={classnames(
                    styles.slide,
                    { [styles.wide]: cardProps?.inline },
                    slideClassName,
                  )}
                >
                  <PromotionCardSkeleton
                    className={classnames(styles.card, cardClassName)}
                    {...(cardProps || {})}
                  />
                </Slide>
              ))
            ) : (
              promotions?.map((promotion, index) => (
                <Slide
                  key={`${promotion.code}${index}`}
                  className={classnames(
                    styles.slide,
                    { [styles.wide]: cardProps?.inline },
                    { [styles.thumbnail]: cardProps?.asThumbnail },
                    { [styles.padded]: withBackground },
                    slideClassName
                  )}
                >
                  {
                    loading
                      ? (
                        <PromotionCardSkeleton
                          className={classnames(styles.card, cardClassName)}
                          {...cardProps}
                        />
                      ) : (
                        <PromotionCard
                          className={classnames(styles.card, cardClassName)}
                          promotion={promotion}
                          selected={promotion?.code === selectedCode}
                          fill={fill}
                          onEnter={() => {
                            onPromotionEnter(promotion, index);
                          }}
                          onClick={() => {
                            onPromotionSelect(promotion, index);
                            onSelect(promotion);
                          }}
                          {...cardProps}
                        />
                      )
                  }
                </Slide>
              ))
            )
        }
      </Slider>

    </div>
  );
};

export default PromotionsCarousel;
